import React, { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import apiService from '../services/api';
import { ActionLog, LogsFilters } from '../types';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';

const LogsPage: React.FC = () => {
  const { trainer } = useAuth();
  const navigate = useNavigate();
  const [logs, setLogs] = useState<ActionLog[]>([]);
  const [trainers, setTrainers] = useState<{ id: number; name: string; email: string }[]>([]);
  const [actionTypes, setActionTypes] = useState<string[]>([]);
  const [resourceTypes, setResourceTypes] = useState<string[]>([]);
  const [filters, setFilters] = useState<LogsFilters>({ limit: 100, offset: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Ждем загрузки данных о тренере
    if (trainer && !trainer.is_admin) {
      navigate('/dashboard');
    }
  }, [trainer, navigate]);

  useEffect(() => {
    apiService.getTrainersForLogs().then(setTrainers);
    apiService.getActionTypes().then(setActionTypes);
    apiService.getResourceTypes().then(setResourceTypes);
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(null);
    apiService.getActionLogs(filters)
      .then(setLogs)
      .catch(e => setError(e.detail || 'Ошибка загрузки логов'))
      .finally(() => setLoading(false));
  }, [filters]);

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value || undefined, offset: 0 }));
  };

  // Показываем загрузку, пока данные о тренере не загружены
  if (!trainer) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-5xl mx-auto py-8 px-4">
          <div className="text-center py-8 text-gray-500">Загрузка...</div>
        </div>
      </div>
    );
  }

  // Проверяем права администратора
  if (!trainer.is_admin) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-5xl mx-auto py-8 px-4">
          <div className="text-center py-8 text-red-500">Доступ запрещен. Требуются права администратора.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="max-w-5xl mx-auto py-8 px-4">
      <h1 className="text-2xl font-bold mb-6">Логи действий тренеров</h1>
      <div className="bg-white rounded shadow p-4 mb-6 flex flex-wrap gap-4 items-end">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Тренер</label>
          <select name="trainer_id" value={filters.trainer_id || ''} onChange={handleFilterChange} className="border rounded px-2 py-1">
            <option value="">Все</option>
            {trainers.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Тип действия</label>
          <select name="action_type" value={filters.action_type || ''} onChange={handleFilterChange} className="border rounded px-2 py-1">
            <option value="">Все</option>
            {actionTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Тип ресурса</label>
          <select name="resource_type" value={filters.resource_type || ''} onChange={handleFilterChange} className="border rounded px-2 py-1">
            <option value="">Все</option>
            {resourceTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Дата с</label>
          <input type="date" name="start_date" value={filters.start_date || ''} onChange={handleFilterChange} className="border rounded px-2 py-1" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Дата по</label>
          <input type="date" name="end_date" value={filters.end_date || ''} onChange={handleFilterChange} className="border rounded px-2 py-1" />
        </div>
      </div>
      {loading ? (
        <div className="text-center py-8 text-gray-500">Загрузка...</div>
      ) : error ? (
        <div className="text-center py-8 text-red-500">
          {typeof error === 'string' ? error : 'Ошибка загрузки логов'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white rounded shadow">
            <thead>
              <tr className="bg-gray-100 text-xs text-gray-600">
                <th className="px-2 py-2">Дата</th>
                <th className="px-2 py-2">Тренер</th>
                <th className="px-2 py-2">Действие</th>
                <th className="px-2 py-2">Ресурс</th>
                <th className="px-2 py-2">Описание</th>
                <th className="px-2 py-2">IP</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-8 text-gray-400">Нет логов</td></tr>
              ) : logs.map(log => (
                <tr key={log.id} className="border-b hover:bg-gray-50 text-xs">
                  <td className="px-2 py-1 whitespace-nowrap">{new Date(log.created_at).toLocaleString()}</td>
                  <td className="px-2 py-1 whitespace-nowrap">{log.trainer_name || '-'}</td>
                  <td className="px-2 py-1 whitespace-nowrap">{log.action_type}</td>
                  <td className="px-2 py-1 whitespace-nowrap">{log.resource_type} {log.resource_name && <span className="text-gray-400">({log.resource_name})</span>}</td>
                  <td className="px-2 py-1">{log.description || '-'}</td>
                  <td className="px-2 py-1 whitespace-nowrap">{log.ip_address || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </div>
    </div>
  );
};

export default LogsPage; 